/**
 * This file includes code that is a derivative work of the following open source  project:
 *
 * Name: '@okta/auth-js'
 * URL: https://github.com/okta/okta-auth-js
 * License: Apache 2.0 (https://www.apache.org/licenses/LICENSE-2.0)
 *
 * The code as been modified to meet the requirements of this project.
 */

import type { CookieOptions, SimpleStorage, StorageType, StorageUtils } from './storage.types';
import { SSO_STORAGE_KEYS, STORAGE_COOKIE_EXPIRATION } from './storage.const';
import { assert, exhaustiveMatch } from '../lib/assert';
import { inMemoryStorage } from './in-memory-storage';
import { Logger } from '../lib/logger';
import { LoggerNames } from '../lib/constants';
import { AuthSdkError } from '../lib/auth-sdk-error';
import { isBrowser } from '../lib/browser';
import { CookieStorage, ICookieStorage } from './cookie-storage';

export const storageUtils: StorageUtils = {
  /**
   * Given a list of storage types, it will test each in order
   * and return the first available storage type.
   *
   * @param storageTypes
   * @throws {AuthSdkError} - No compatible storage type found
   */
  findStorageType(storageTypes: Array<StorageType>): StorageType {
    const storageType = storageTypes.find((storageType) => {
      const logger = Logger.createLogger(LoggerNames.STORAGE_UTILS);
      const supportsStorageType = this.testStorageType(storageType);
      if (!supportsStorageType) {
        logger.warn(`Storage type ${storageType} is not supported`);
      }
      return supportsStorageType;
    });
    if (!storageType) {
      throw new AuthSdkError('No storage type available');
    }
    return storageType;
  },
  /**
   * Returns a storage interface for the given storage type.
   * @param storageType
   * @param cookieOptions
   */
  getStorageByType(storageType, cookieOptions): SimpleStorage {
    switch (storageType) {
      case 'localStorage':
        return this.getLocalStorage();
      case 'sessionStorage':
        return this.getSessionStorage();
      case 'cookie':
        return this.getCookieStorage(cookieOptions);
      case 'memory':
        return inMemoryStorage;
      default:
        return exhaustiveMatch(storageType, new AuthSdkError(`Unknown storage type: ${storageType}`));
    }
  },
  /**
   * Tests whether the given storage type is supported.
   * @param storageType
   */
  testStorageType(storageType: StorageType): boolean {
    switch (storageType) {
      case 'localStorage':
        return hasLocalStorage();
      case 'sessionStorage':
        return hasSessionStorage();
      case 'cookie':
      case 'memory':
        return true;
      default:
        return exhaustiveMatch(storageType, new AuthSdkError(`Unknown storage type: ${storageType}`));
    }
  },

  getLocalStorage(): SimpleStorage {
    return window.localStorage;
  },

  getSessionStorage(): SimpleStorage {
    return window.sessionStorage;
  },

  getCookieStorage(options: CookieOptions): ICookieStorage {
    options.path ||= '/';
    options.secure ||= true;
    options.sameSite ||= options.secure ? 'none' : 'lax';
    options.expires ||= options.sessionCookie ? undefined : new Date(Date.now() + STORAGE_COOKIE_EXPIRATION);
    return new CookieStorage(options);
  },
};

/**
 * Given a storage interface, tests that it is usable (doesn't throw errors when accessed)
 * @param storage
 */
function testStorage(storage: SimpleStorage) {
  try {
    storage.setItem(SSO_STORAGE_KEYS.TEST, SSO_STORAGE_KEYS.TEST);
    assert(storage.getItem(SSO_STORAGE_KEYS.TEST) === SSO_STORAGE_KEYS.TEST);
    storage.removeItem(SSO_STORAGE_KEYS.TEST);
    return true;
  } catch {
    return false;
  }
}

/**
 * Tests whether localStorage is available
 */
function hasLocalStorage(): boolean {
  try {
    return isBrowser() && testStorage(window.localStorage);
  } catch {
    return false;
  }
}

/**
 * Tests whether sessionStorage is available
 */
function hasSessionStorage(): boolean {
  try {
    return isBrowser() && testStorage(window.sessionStorage);
  } catch {
    return false;
  }
}
